/**
 * Cart Enhancement Utilities
 * Provides enhanced cart functionality for metafields and services
 */
class CartEnhancements {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.enhanceVariantDisplay();
      this.initializeServiceManagement();
    });
  }

  /**
   * Safe JSON parsing with error handling
   * @param {string} jsonString - JSON string to parse
   * @param {*} fallback - Fallback value if parsing fails
   * @returns {*} Parsed object or fallback
   */
  safeJsonParse(jsonString, fallback = null) {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('JSON parsing failed:', e);
      return fallback;
    }
  }

  /**
   * Format money consistently
   * @param {number} pence - Amount in pence
   * @returns {string} Formatted money string
   */
  formatMoney(pence) {
    if (window.MoneyFormatter) {
      return window.MoneyFormatter.format(pence);
    }
    // Fallback formatting
    const amount = pence / 100;
    return `£${amount.toFixed(2)}`;
  }

  /**
   * Enhance variant options display with better formatting
   */
  enhanceVariantDisplay() {
    const variantOptions = document.querySelectorAll('.variant-option-item');
    
    variantOptions.forEach(option => {
      const priceTag = option.querySelector('.option-price-tag, .option-included-tag');
      if (priceTag && priceTag.textContent.includes('undefined')) {
        priceTag.textContent = 'included';
        priceTag.className = 'option-included-tag';
      }
    });
  }

  /**
   * Initialize service management features
   */
  initializeServiceManagement() {
    // Handle service removal from cart
    const removeButtons = document.querySelectorAll('.service-remove');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.removeServiceFromCart(button);
      });
    });
  }

  /**
   * Remove service from cart
   * @param {HTMLElement} button - Remove button element
   */
  removeServiceFromCart(button) {
    const serviceId = button.dataset.serviceId;
    const serviceName = button.title.replace('Remove ', '');
    
    if (!serviceId) {
      console.error('No service ID found');
      return;
    }

    // Show loading state
    button.textContent = '...';
    button.disabled = true;

    // Get current cart
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Find service line item
        const serviceItem = cart.items.find(item => 
          item.properties && 
          item.properties._service === 'true' && 
          (item.properties._service_type === serviceId || item.properties._service_id === serviceId)
        );

        if (!serviceItem) {
          throw new Error('Service not found in cart');
        }

        // Remove the line item
        const lineIndex = cart.items.indexOf(serviceItem) + 1;
        
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: 0
          })
        });
      })
      .then(response => response.json())
      .then(() => {
        this.showNotification(`${serviceName} removed from cart`, 'success');
        // Reload page to reflect changes
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch(error => {
        console.error('Error removing service:', error);
        button.textContent = '×';
        button.disabled = false;
        this.showNotification('Error removing service', 'error');
      });
  }

  /**
   * Show notification to user
   * @param {string} message - Notification message
   * @param {string} type - Notification type (success, error)
   */
  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#28a745' : '#dc3545'};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-weight: 600;
      z-index: 10000;
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    // Auto remove
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Initialize cart enhancements
new CartEnhancements();

console.log('🛒 Cart enhancements loaded');
